import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Globe, Save, RefreshCw } from 'lucide-react';
import { siteSettingsAPI } from '../../services/api';
import AdminLayout from '../../components/admin/AdminLayout';
import { toast } from '../../hooks/use-toast';

const AdminSEO = () => {
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await siteSettingsAPI.get();
        setMetaTitle(data.meta_title || '');
        setMetaDescription(data.meta_description || '');
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await siteSettingsAPI.update({ meta_title: metaTitle, meta_description: metaDescription });
      toast({ title: 'Guardado', description: 'Configuración SEO actualizada correctamente' });
    } catch (err) {
      toast({ title: 'Error', description: 'No se pudo guardar', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-white" data-testid="admin-seo-title">SEO</h1>
          <p className="text-gray-400 mt-1">Configuración global de meta tags para motores de búsqueda</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-48">
            <RefreshCw className="w-8 h-8 text-orange-500 animate-spin" />
          </div>
        ) : (
          <Card className="bg-gray-800 border-gray-700 p-6 max-w-2xl">
            <div className="flex items-center gap-3 mb-6">
              <Globe className="w-6 h-6 text-orange-400" />
              <h2 className="text-lg font-semibold text-white">Meta Tags Globales</h2>
            </div>

            <div className="space-y-5">
              <div>
                <Label className="text-gray-300 mb-1 block">Meta Título</Label>
                <Input
                  data-testid="seo-meta-title"
                  value={metaTitle}
                  onChange={(e) => setMetaTitle(e.target.value)}
                  placeholder="Zona Libre - Tu marketplace favorito"
                  className="bg-gray-700 border-gray-600 text-white"
                  maxLength={70}
                />
                <p className="text-xs text-gray-500 mt-1">{metaTitle.length}/70 caracteres</p>
              </div>

              <div>
                <Label className="text-gray-300 mb-1 block">Meta Descripción</Label>
                <Textarea
                  data-testid="seo-meta-description"
                  value={metaDescription}
                  onChange={(e) => setMetaDescription(e.target.value)}
                  placeholder="Encuentra los mejores productos al mejor precio en Zona Libre..."
                  className="bg-gray-700 border-gray-600 text-white min-h-[100px]"
                  maxLength={160}
                />
                <p className="text-xs text-gray-500 mt-1">{metaDescription.length}/160 caracteres</p>
              </div>

              {/* Preview */}
              <div className="border border-gray-600 rounded-lg p-4 bg-gray-900">
                <p className="text-xs text-gray-500 mb-2">Vista previa en buscador:</p>
                <p className="text-blue-400 text-base font-medium truncate">{metaTitle || 'Zona Libre'}</p>
                <p className="text-green-400 text-xs mb-1">zonalibre.com</p>
                <p className="text-gray-400 text-sm line-clamp-2">{metaDescription || 'Sin descripción configurada.'}</p>
              </div>

              <Button
                data-testid="seo-save-btn"
                onClick={handleSave}
                disabled={saving}
                className="bg-orange-500 hover:bg-orange-600"
              >
                {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {saving ? 'Guardando...' : 'Guardar cambios'}
              </Button>
            </div>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminSEO;
